import axios from "axios";
import { getSessionToken } from "@descope/react-sdk";
import { AppMode } from "@/lib/config/types";

interface StoredConfig {
  mode?: AppMode;
  localBaseUrl?: string;
  localTenantId?: string;
  [key: string]: unknown;
}

const getStoredConfig = (): StoredConfig | null => {
  try {
    const stored = localStorage.getItem("harmony-management-config");
    if (stored) {
      return JSON.parse(stored) as StoredConfig;
    }
  } catch (error) {
    console.error("Failed to load config from localStorage:", error);
  }
  return null;
};

export const serviceCatalogClient = axios.create();

export const configureServiceCatalogClient = (): void => {
  try {
    const config = getStoredConfig();
    const mode = config?.mode ?? AppMode.REMOTE;

    let baseUrl: string;
    if (mode === AppMode.LOCAL) {
      const localBaseUrl = config?.localBaseUrl ?? "http://localhost:8000";
      baseUrl = `${localBaseUrl}/api/v1`;
    } else if (import.meta.env.DEV) {
      baseUrl = "/service-catalog/api/v1";
    } else {
      baseUrl =
        (import.meta.env.VITE_SERVICE_CATALOG_API_URL as string | undefined) ??
        `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/service-catalog/api/v1`;
    }

    serviceCatalogClient.defaults.baseURL = baseUrl;
    serviceCatalogClient.interceptors.request.use((v) => {
      try {
        if (mode === AppMode.REMOTE) {
          const token = getSessionToken();
          if (token) {
            v.headers.set("Authorization", `Bearer ${token}`);
          }
        } else {
          const localTenantId = config?.localTenantId;
          if (localTenantId) {
            v.headers.set("x-tenant-id", localTenantId);
          }
        }

        if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TENANT_ID && !v.headers.get("x-tenant-id")) {
          v.headers.set("x-tenant-id", import.meta.env.VITE_FORCE_TENANT_ID);
        }
      } catch (error: unknown) {
        console.error("Failed to configure Service Catalog request headers:", error);
      }
      return v;
    });
  } catch (error) {
    console.error("Failed to configure Service Catalog client:", error);
  }
};

configureServiceCatalogClient();
